import React, { Suspense, useEffect, useState } from "react";
import { AppBar, Toolbar, Button } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import { useDispatch, useSelector } from "react-redux";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import emptyProfile from "@/images/empty_profile.png";
import IconButton from "@mui/material/IconButton";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DraftsIcon from "@mui/icons-material/Drafts";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import Tooltip from "@mui/material/Tooltip";
import { setHandleRedirect, showSideNav } from "@/store/sideNav/sideNavActions";
import { withTranslation } from "react-i18next";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import SettingsIcon from "@mui/icons-material/Settings";
import HelpIcon from "@mui/icons-material/Help";
import LogoutIcon from "@mui/icons-material/Logout";
import CheckIcon from "@mui/icons-material/Check";
import LanguageIcon from "@mui/icons-material/Language";
import tokens from "@/theme/tokens";
import radii from "@/theme/radii";
import Badge from "@mui/material/Badge";
import {
  buildInvitationsDropdownOpenedEvent,
  buildNotificationsDropdownOpenedEvent,
  track,
} from "@/analytics";
import { doLogout } from "@/store/logout/logoutActions";
import { TopbarHeight } from "@/theme/Theme-variable";
import { setUserPreference } from "@/store/user/userActions";
import { getLanguageLabel } from "@/utils/string";
import useUserInvites from "@/hooks/useUserInvites";
import CircularProgress from "@mui/material/CircularProgress";
import { lazyWithRetry as lazy } from "@/utils/lazyWithRetry";
import i18n from "../../../../i18n";
import PublicHeader from "./PublicHeader";
import HeaderBreadcrumb from "./HeaderBreadcrumb";
import HeaderSearch from "./HeaderSearch";

/**
 * The two dropdown bodies are whole pages — /notifications and /invitations,
 * reused here as panels. Imported statically they landed in the entry chunk on
 * every route, and worse, they defeated the route-level `lazy()` in lazyRoutes:
 * Rollup reported both as INEFFECTIVE_DYNAMIC_IMPORT, so those routes were not
 * being split either. Declared at module scope, not in the body — a lazy
 * component created during render is a new type every render.
 */
const NotificationDropdown = lazy(() => import("@/Pages/Notifications"));
const InvitesDropdown = lazy(() => import("@/Pages/Invitations"));

/**
 * Placeholder while a dropdown's chunk arrives. Sized so the menu paper, whose
 * height is fixed by the sx below, does not visibly reflow around it.
 */
const DropdownLoading = () => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%",
      minHeight: "120px",
    }}
  >
    <CircularProgress />
  </Box>
);

const Header = ({ sx, t }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [notifyEl, setNotifyEl] = React.useState(null);
  const [invitesEl, setInvitesEl] = React.useState(null);
  // Both Menus keep their paper mounted (`keepMounted`), so gating on the
  // anchor alone would still mount — and therefore still fetch — the body of a
  // dropdown the user never opens. These latch on first open and stay true, so
  // after that the panels behave exactly as keepMounted ones did.
  const [notifyEverOpened, setNotifyEverOpened] = React.useState(false);
  const [invitesEverOpened, setInvitesEverOpened] = React.useState(false);

  React.useEffect(() => {
    if (notifyEl) setNotifyEverOpened(true);
  }, [notifyEl]);

  React.useEffect(() => {
    if (invitesEl) setInvitesEverOpened(true);
  }, [invitesEl]);
  const [toggleLang, setToggleLang] = React.useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("ENG");
  const dispatch = useDispatch();
  const { user, profile, sideNav } = useSelector((state) => state);
  const firstName = user?.get("first_name") || "";
  const avatarURL = profile.avatar || emptyProfile;
  const isLoggedIn = user?.get("jwt") ?? false;
  const isSideNavOpen = sideNav?.get("menuOpen") ?? false;
  const currentLanguage =
    getLanguageLabel(user?.get("user_preference")?.get("language")) ??
    "English";
  const inviteInfo = useUserInvites();
  const userPreferredLanguage = useSelector(
    (state) =>
      state.user &&
      state.user.get("user_preference") &&
      state.user.get("user_preference").get("language")
  );

  const handleRedirect = (pId, url, hId) => {
    dispatch(setHandleRedirect(pId, url, hId));
    setAnchorEl(null);
  };

  const logout = () => {
    dispatch(doLogout());
    setAnchorEl(null);
  };

  const closeNavBar = () => {
    if (isLoggedIn) {
      dispatch(showSideNav());
    }
  };

  const selectLanguage = (language) => {
    setSelectedLanguage(language);
    dispatch(setUserPreference(language));
  };

  useEffect(() => {
    if (userPreferredLanguage === "ESP") {
      i18n.changeLanguage("es");
    } else {
      i18n.changeLanguage("en");
    }
    setSelectedLanguage(userPreferredLanguage);
  }, [userPreferredLanguage]);

  return (
    <AppBar
      color="primary"
      sx={{
        ...sx,
        top: { xs: isLoggedIn ? "auto" : 0, md: 0 },
        bottom: { xs: isLoggedIn ? 0 : "auto", md: "auto" },
        position: { xs: isLoggedIn ? "fixed" : "sticky", md: "sticky" },
        flex: "0 0 auto",
      }}
    >
      <Toolbar
        sx={{
          justifyContent: { sm: "space-between", xs: "space-between" },
        }}
      >
        {isLoggedIn && (
          <>
            {!isSideNavOpen && (
              <Box
                onClick={closeNavBar}
                aria-label="menu"
                aria-controls="navbar-menu"
                aria-haspopup="true"
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  cursor: "pointer",
                  px: 1,
                }}
              >
                <MenuIcon color="secondary" />
                <Typography
                  variant="h6"
                  color="secondary"
                  sx={{
                    display: {
                      xs: "none",
                      sm: "flex",
                    },
                  }}
                >
                  {t("Menu")}
                </Typography>
              </Box>
            )}
            {/* Whatever page is on screen publishes this; empty on pages that
                don't. See BreadcrumbContext. */}
            <HeaderBreadcrumb />
            <HeaderSearch />
            <Box
              sx={{
                flexGrow: 1,

                display: {
                  xs: "none",
                  sm: "flex",
                }
              }} />
            <Box
              onClick={() => handleRedirect(null, `/profile/${user.get("id")}`)}
              aria-label="menu"
              aria-controls="profile-menu"
              aria-haspopup="true"
              sx={{
                color: "secondary.main",
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                cursor: "pointer",
                px: 1
              }}>
              <Avatar
                src={avatarURL}
                alt={avatarURL}
                sx={{
                  width: "30px",
                  height: "30px",
                }}
              />
              <Typography
                variant="h6"
                color="secondary"
                sx={{
                  display: {
                    xs: "none",
                    sm: "flex",
                  },
                  ml: 1,
                }}
              >
                {firstName}
              </Typography>
            </Box>
            <Box
              sx={{
                width: "1px",
                backgroundColor: tokens.color.hairlineShadow,
                height: "25px",
                ml: 1,
                mr: 1,
                display: { sm: "none", xs: "none", lg: "block" },
              }}
            />
            <Tooltip title={t("Invitations")}>
              <IconButton
                onClick={($event) => {
                  track(buildInvitationsDropdownOpenedEvent(inviteInfo?.inviteCount ?? 0));
                  setInvitesEl($event.currentTarget);
                }}
                color="secondary"
                aria-label="Invitations"
                sx={{
                  marginLeft: { xs: 0, md: 1 },
                  marginRight: { xs: 0, md: 1 },
                  minWidth: { xs: 64, md: 64, lg: "auto" },
                }}
              >
                <Badge badgeContent={inviteInfo?.inviteCount} color="secondary">
                  <DraftsIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Menu
              id="invites-menu"
              anchorEl={invitesEl}
              keepMounted
              open={Boolean(invitesEl)}
              onClose={() => setInvitesEl(null)}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              sx={{
                zIndex: 900,
                "& .MuiMenu-paper": {
                  width: "450px",
                  maxHeight: "550px",
                  height: "550px",
                  overflowY: "auto",
                  right: 20,
                  left: "auto !important",
                  top: {
                    xs: "auto !important",
                    sm: `auto !important`,
                    md: `${TopbarHeight}px !important`,
                    lg: `${TopbarHeight}px !important`,
                  },
                  bottom: {
                    xs: "56px !important",
                    sm: "56px !important",
                    md: "auto !important",
                    lg: "auto !important",
                  },
                  backgroundColor: (theme) => theme.palette.grey["100"],
                },
                "& .MuiList-padding": {
                  p: tokens.space.headerListPadding,
                },
              }}
            >
              {invitesEverOpened && (
                <Suspense fallback={<DropdownLoading />}>
                  <InvitesDropdown
                    isDropdown
                    invites={inviteInfo?.invites}
                    isOpened={Boolean(invitesEl)}
                    onClose={() => setInvitesEl(null)}
                  />
                </Suspense>
              )}
            </Menu>
            <Tooltip title={t("Notifications")}>
              <IconButton
                onClick={($event) => {
                  track(buildNotificationsDropdownOpenedEvent(inviteInfo?.notifyCount ?? 0));
                  setNotifyEl($event.currentTarget);
                }}
                color="secondary"
                aria-label="Invites"
                sx={{
                  marginLeft: { xs: 0, md: 1 },
                  marginRight: { xs: 0, md: 1 },
                  minWidth: { xs: 64, md: 64, lg: "auto" },
                }}
              >
                <Badge color="secondary" badgeContent={inviteInfo?.notifyCount}>
                  <NotificationsNoneIcon />
                </Badge>
              </IconButton>
            </Tooltip>
            <Menu
              id="notify-menu"
              anchorEl={notifyEl}
              keepMounted
              open={Boolean(notifyEl)}
              onClose={() => setNotifyEl(null)}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              sx={{
                zIndex: 900,
                "& .MuiMenu-paper": {
                  boxShadow: 1,
                  width: "326px",
                  overflowY: "auto",
                  right: 20,
                  left: "auto !important",
                  top: {
                    xs: "auto !important",
                    sm: `auto !important`,
                    md: `${TopbarHeight}px !important`,
                    lg: `${TopbarHeight}px !important`,
                  },
                  bottom: {
                    xs: "56px !important",
                    sm: "56px !important",
                    md: "auto !important",
                    lg: "auto !important",
                  },
                },
              }}
            >
              {notifyEverOpened && (
                <Suspense fallback={<DropdownLoading />}>
                  <NotificationDropdown
                    isMenuOpened={Boolean(notifyEl)}
                    isDropdown
                    close={() => setNotifyEl(null)}
                  />
                </Suspense>
              )}
            </Menu>
            <Tooltip title={t("More")}>
              <IconButton
                onClick={($event) => setAnchorEl($event.currentTarget)}
                color="secondary"
                size="small"
                aria-label="More"
                sx={{
                  marginLeft: { xs: 0, md: 1 },
                  marginRight: { xs: 0, md: 1 },
                  display: { sm: "none", xs: "none", md: "inline-flex" },
                }}
              >
                <ArrowDropDownIcon />
              </IconButton>
            </Tooltip>
            <Menu
              id="more-menu"
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={() => setAnchorEl(null)}
              sx={{
                "& .MuiMenu-paper": {
                  width: "280px",
                  right: 0,
                  top: "56px !important",
                  borderRadius: `${radii.xl}px`,
                },
                "& .MuiList-padding": {
                  p: tokens.space.px0,
                },
              }}
            >
              <MenuItem
                onClick={() => {
                  handleRedirect(null, "/me/settings");
                }}
                sx={{
                  pt: 3,
                  pb: 3,
                  padding: tokens.space.px10,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center"
                  }}>
                  <Button
                    color="secondary"
                    sx={{
                      boxShadow: "none",
                      minWidth: "50px",
                      width: "45px",
                      height: "40px",
                      borderRadius: `${radii.md}px`,
                    }}
                  >
                    <SettingsIcon width="18" height="18" />
                  </Button>
                  <Box
                    sx={{
                      ml: 2,
                    }}
                  >
                    <Typography variant="h5">{t("settings")}</Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem
                href="https://rebelbase.zendesk.com/hc/en-us"
                target="_blank"
                rel="noopener noreferrer"
                component="a"
                sx={{
                  pt: 3,
                  pb: 3,
                  padding: tokens.space.px10,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center"
                  }}>
                  <Button
                    color="secondary"
                    sx={{
                      boxShadow: "none",
                      minWidth: "50px",
                      width: "45px",
                      height: "40px",
                      borderRadius: `${radii.md}px`,
                    }}
                  >
                    <HelpIcon width="18" height="18" />
                  </Button>
                  <Box
                    sx={{
                      ml: 2,
                    }}
                  >
                    <Typography variant="h5">{t("help")}</Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => setToggleLang(!toggleLang)}
                sx={{
                  pt: 3,
                  pb: 3,
                  padding: tokens.space.px10,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: 1
                  }}>
                  <Button
                    color="secondary"
                    sx={{
                      boxShadow: "none",
                      minWidth: "50px",
                      width: "45px",
                      height: "40px",
                      borderRadius: `${radii.md}px`,
                    }}
                  >
                    <LanguageIcon width="18" height="18" />
                  </Button>
                  <Box
                    sx={{
                      ml: 2,
                    }}
                  >
                    <Typography variant="h5">{t("Language")}</Typography>
                  </Box>
                  <Box sx={{
                    flexGrow: 1
                  }} />
                  {!toggleLang ? (
                    <IconButton
                      color="secondary"
                      size="small"
                      aria-label="More"
                    >
                      <ExpandMoreIcon />
                    </IconButton>
                  ) : (
                    <IconButton
                      color="secondary"
                      size="small"
                      aria-label="Less"
                    >
                      <ExpandLessIcon />
                    </IconButton>
                  )}
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  selectLanguage("ENG");
                  setAnchorEl(null);
                }}
                disabled={selectedLanguage === "ENG"}
                sx={{
                  pt: 3,
                  pb: 3,
                  padding: tokens.space.px10,
                  display: toggleLang ? "flex" : "none",
                  justifyContent: "flex-start",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center"
                  }}>
                  <Box
                    sx={{
                      ml: 2,
                      pl: selectedLanguage !== "ENG" ? 6 : 0,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {selectedLanguage === "ENG" && (
                      <IconButton
                        color="secondary"
                        size="small"
                        aria-label="More"
                      >
                        <CheckIcon />
                      </IconButton>
                    )}
                    <Typography variant="h5">English</Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  selectLanguage("ESP");
                  setAnchorEl(null);
                }}
                disabled={selectedLanguage === "ESP"}
                sx={{
                  pt: 3,
                  pb: 3,
                  padding: tokens.space.px10,
                  display: toggleLang ? "flex" : "none",
                  justifyContent: "flex-start",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center"
                  }}>
                  <Box
                    sx={{
                      ml: 2,
                      pl: selectedLanguage !== "ESP" ? 6 : 0,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {selectedLanguage === "ESP" && (
                      <IconButton
                        color="secondary"
                        size="small"
                        aria-label="More"
                      >
                        <CheckIcon />
                      </IconButton>
                    )}
                    <Typography variant="h5">Español</Typography>
                  </Box>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={logout}
                sx={{
                  pt: 3,
                  pb: 3,
                  padding: tokens.space.px10,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center"
                  }}>
                  <Button
                    color="secondary"
                    sx={{
                      boxShadow: "none",
                      minWidth: "50px",
                      width: "45px",
                      height: "40px",
                      borderRadius: `${radii.md}px`,
                    }}
                  >
                    <LogoutIcon width="18" height="18" />
                  </Button>
                  <Box
                    sx={{
                      ml: 2,
                    }}
                  >
                    <Typography variant="h5">{t("logOut")}</Typography>
                  </Box>
                </Box>
              </MenuItem>
            </Menu>
          </>
        )}
        {!isLoggedIn && (
          <>
            <PublicHeader
              currentLanguage={currentLanguage}
              closeNavBar={closeNavBar}
            />
          </>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default withTranslation(["translation.navigation"])(React.memo(Header));
